import { useState, useEffect, useCallback, useRef } from "react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { processStreamEvent } from "~/components/assistant/lib/messageProcessor";
import { readStreamEvents } from "~/components/assistant/lib/streamReader";
import type {
  MCPServer,
  Message,
  Conversation,
  MessageFeedbackCreate,
  PartialSkill,
  SkillCreate,
  SkillUpdate,
} from "~/components/assistant/types";
import { useConversations } from "~/contexts/conversations";
import { trackEvent } from "~/utils/monitoring";

export interface UseAssistantOptions {
  /**
   * The API implementation to use for chat operations.
   */
  api: AssistantApi;

  /**
   * The context ID (e.g., dataset ID, project ID).
   */
  contextId: string;

  /**
   * The context title (e.g., dataset name, project name).
   */
  contextTitle?: string;

  /**
   * Whether debug mode is enabled (shows hidden tools).
   * @default false
   */
  debugMode?: boolean;

  /**
   * Whether skills feature is enabled for this user.
   * When false, skills will not be loaded from the API.
   * @default false
   */
  skillsEnabled?: boolean;

  /**
   * Callback invoked when filters are received from the filter builder agent.
   * Used to auto-apply filters to the cohort.
   */
  onFiltersReceived?: (filters: unknown) => void;
}

/**
 * Main hook for assistant functionality.
 * Manages messages, conversations, MCP servers, and streaming.
 */
export function useAssistant(options: UseAssistantOptions) {
  const {
    api,
    contextId,
    contextTitle,
    debugMode = false,
    skillsEnabled = false,
    onFiltersReceived,
  } = options;

  const conversationsContext = useConversations();

  // NOTE: Loading states are split between local and context:
  // - isLoadingContextHistory: Local, tracks history loading for current conversation
  // - isCreatingConversation: Local, tracks UI action of creating new conversation
  // - isStreaming: Context, tracks active message stream per conversation
  // This split keeps UI-specific state (creating) separate from conversation state (streaming).

  // Local loading state for context-based history loading
  const [isLoadingContextHistory, setIsLoadingContextHistory] = useState(true);

  // Conversation state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);

  // Ref to track current conversation for async operations (avoids stale closure issues)
  const currentConversationIdRef = useRef<string | null>(null);
  currentConversationIdRef.current = currentConversationId;

  // Read messages from context. Component re-renders when context value changes.
  const messages = conversationsContext?.getMessages(currentConversationId ?? "") ?? [];

  // MCP Server state
  const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
  const [selectedMcpServers, setSelectedMcpServers] = useState<string[]>([]);

  // Skills state
  const [systemSkills, setSystemSkills] = useState<PartialSkill[]>([]);
  const [userSkills, setUserSkills] = useState<PartialSkill[]>([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(false);

  // Cancel state - prevents multiple cancel clicks
  const [isCancelling, setIsCancelling] = useState(false);

  // Message helper functions
  const nextMessageId = useCallback(
    (prefix: string) => {
      return `${prefix}-${contextId}-${crypto.randomUUID()}`;
    },
    [contextId]
  );

  const clearMessages = useCallback(() => {
    if (currentConversationId && conversationsContext) {
      conversationsContext.setMessages(currentConversationId, []);
    }
  }, [currentConversationId, conversationsContext]);

  // Conversation helper functions
  const loadConversations = useCallback(
    async (contextId: string): Promise<Conversation[]> => {
      try {
        const loaded = await api.listConversations(contextId);
        setConversations(loaded);
        return loaded;
      } catch (error) {
        console.error("Failed to load conversations:", error);
        throw error;
      }
    },
    [api]
  );

  const createConversation = useCallback(
    async (trigger: "auto" | "manual" = "auto"): Promise<Conversation> => {
      setIsCreatingConversation(true);
      try {
        const existingCount = conversations.length;
        const newConv = await api.createConversation(contextId, contextTitle);
        setCurrentConversationId(newConv.id);
        // Reload conversations to include the new one
        await loadConversations(contextId);

        trackEvent("analysis_agent_conversation_created", {
          dataset_id: contextId || "Unknown",
          conversation_id: newConv.id,
          context: "dataset",
          trigger,
          existing_conversations_count: existingCount,
        });

        return newConv;
      } catch (error) {
        console.error("Failed to create conversation:", error);
        throw error;
      } finally {
        setIsCreatingConversation(false);
      }
    },
    [api, contextId, contextTitle, loadConversations, conversations.length]
  );

  const deleteConversation = useCallback(
    async (conversationId: string, e: React.MouseEvent): Promise<void> => {
      e.preventDefault();
      e.stopPropagation();

      // Get conversation metadata before deleting
      const conversationToDelete = conversations.find((c) => c.id === conversationId);

      try {
        await api.deleteConversation(conversationId);
        const updated = conversations.filter((c) => c.id !== conversationId);
        setConversations(updated);

        // Track deletion
        if (conversationToDelete && contextId) {
          trackEvent("analysis_agent_conversation_deleted", {
            dataset_id: contextId,
            conversation_id: conversationId,
            context: "dataset",
            message_count: conversationToDelete.message_count || 0,
          });
        }

        // Auto-select logic
        if (currentConversationId === conversationId) {
          if (updated.length > 0) {
            // Select most recent remaining conversation
            setCurrentConversationId(updated[0].id);
          } else {
            // No conversations left - create new one
            setCurrentConversationId(null);
            await createConversation();
          }
        }
      } catch (error) {
        console.error("Failed to delete conversation:", error);
        throw error;
      }
    },
    [api, conversations, currentConversationId, createConversation, contextId]
  );

  const selectConversation = useCallback(
    (conversationId: string) => {
      const selectedConversation = conversations.find((c) => c.id === conversationId);

      trackEvent("analysis_agent_conversation_selected", {
        dataset_id: contextId || "Unknown",
        conversation_id: conversationId,
        context: "dataset",
        from_conversation_id: currentConversationId || undefined,
        message_count: selectedConversation?.message_count || 0,
      });

      // Set loading immediately to prevent placeholder from flashing
      setIsLoadingContextHistory(true);
      setCurrentConversationId(conversationId);
    },
    [conversations, contextId, currentConversationId]
  );

  const renameConversation = useCallback(
    async (conversationId: string, displayName: string): Promise<void> => {
      try {
        await api.updateConversation(conversationId, { display_name: displayName });
        // Update local state
        setConversations((prev) =>
          prev.map((c) => (c.id === conversationId ? { ...c, display_name: displayName } : c))
        );

        trackEvent("analysis_agent_conversation_renamed", {
          dataset_id: contextId || "Unknown",
          conversation_id: conversationId,
          context: "dataset",
        });
      } catch (error) {
        console.error("Failed to rename conversation:", error);
        throw error;
      }
    },
    [api, contextId]
  );

  const toggleMcpServer = useCallback(
    (serverId: string) => {
      setSelectedMcpServers((prev) => {
        const isCurrentlySelected = prev.includes(serverId);
        const newSelection = isCurrentlySelected
          ? prev.filter((id) => id !== serverId)
          : [...prev, serverId];

        const server = mcpServers.find((s) => s.id === serverId);

        trackEvent("analysis_agent_mcp_server_toggled", {
          dataset_id: contextId || "Unknown",
          context: "dataset",
          server_id: serverId,
          server_name: server?.name || serverId,
          enabled: !isCurrentlySelected,
          total_selected: newSelection.length,
        });

        return newSelection;
      });
    },
    [mcpServers, contextId]
  );

  // Skills mutation functions
  const loadSkills = useCallback(async () => {
    setIsLoadingSkills(true);
    try {
      const response = await api.listSkills();
      setSystemSkills(response.system);
      setUserSkills(response.user);
    } catch (error) {
      console.error("Failed to load skills:", error);
      setSystemSkills([]);
      setUserSkills([]);
    } finally {
      setIsLoadingSkills(false);
    }
  }, [api]);

  const createSkill = useCallback(
    async (skill: SkillCreate) => {
      try {
        await api.createSkill(skill);
        await loadSkills();
      } catch (error) {
        console.error("Failed to create skill:", error);
        throw error;
      }
    },
    [api, loadSkills]
  );

  const updateSkill = useCallback(
    async (skillId: string, skill: SkillUpdate) => {
      try {
        await api.updateSkill(skillId, skill);
        await loadSkills();
      } catch (error) {
        console.error("Failed to update skill:", error);
        throw error;
      }
    },
    [api, loadSkills]
  );

  const deleteSkill = useCallback(
    async (skillId: string) => {
      try {
        await api.deleteSkill(skillId);
        await loadSkills();
      } catch (error) {
        console.error("Failed to delete skill:", error);
        throw error;
      }
    },
    [api, loadSkills]
  );

  const getSkill = useCallback(
    async (skillId: string) => {
      return api.getSkill(skillId);
    },
    [api]
  );

  // Callback for handling conversation name updates from the stream
  const handleConversationNameUpdate = useCallback((conversationId: string, name: string) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === conversationId ? { ...c, display_name: name } : c))
    );
  }, []);

  const sendMessage = useCallback(
    (prompt: string) => {
      if (!currentConversationId || !conversationsContext) {
        console.warn("Cannot send message: no active conversation");
        return;
      }

      const currentMessages = conversationsContext.getMessages(currentConversationId);
      const isFirstMessage = currentMessages.length === 0;

      // Filter out ui_only servers - they appear in UI but are not sent to backend
      const serversToSend = selectedMcpServers.filter((serverId) => {
        const server = mcpServers.find((s) => s.id === serverId);
        return server && !server.ui_only;
      });

      trackEvent("analysis_agent_message_sent", {
        dataset_id: contextId || "Unknown",
        conversation_id: currentConversationId,
        context: "dataset",
        message_length: prompt.length,
        selected_mcp_servers: serversToSend,
        mcp_server_count: serversToSend.length,
        is_first_message: isFirstMessage,
      });

      conversationsContext.startStream(
        api,
        currentConversationId,
        contextId,
        prompt,
        serversToSend,
        debugMode,
        () => nextMessageId("agent"),
        handleConversationNameUpdate,
        onFiltersReceived
      );
    },
    [
      currentConversationId,
      conversationsContext,
      api,
      contextId,
      selectedMcpServers,
      mcpServers,
      debugMode,
      nextMessageId,
      handleConversationNameUpdate,
      onFiltersReceived,
    ]
  );

  const cancelMessage = useCallback(async () => {
    if (!currentConversationId || !conversationsContext) {
      console.warn("Cannot cancel message: no active conversation");
      return;
    }

    // Prevent multiple cancel requests
    if (isCancelling) {
      return;
    }
    setIsCancelling(true);

    // Extract running tools before cancelling
    const currentMessages = conversationsContext.getMessages(currentConversationId);
    const runningTools: string[] = [];
    currentMessages.forEach((msg) => {
      if (msg.kind === "model_chat" && msg.streaming && msg.parts) {
        msg.parts.forEach((part) => {
          if (part.type === "toolCall" && part.toolCall?.running) {
            runningTools.push(part.toolCall.toolName || "unknown");
          }
        });
      }
    });

    trackEvent("analysis_agent_message_cancelled", {
      dataset_id: contextId || "Unknown",
      conversation_id: currentConversationId,
      context: "dataset",
      tools_running: runningTools,
    });

    await conversationsContext.cancelStream(api, currentConversationId);
    // Note: isCancelling gets reset when streaming ends (see useEffect below)
  }, [currentConversationId, api, conversationsContext, contextId, isCancelling]);

  const submitMessageFeedback = useCallback(
    async (messageId: string, feedback: MessageFeedbackCreate): Promise<void> => {
      if (!currentConversationId) {
        console.warn("Cannot submit feedback: no active conversation");
        return;
      }

      const result = await api.putMessageFeedback(currentConversationId, messageId, feedback);

      // Update message state in context
      if (conversationsContext) {
        const currentMessages = conversationsContext.getMessages(currentConversationId);
        const updatedMessages = currentMessages.map((msg) =>
          msg.id === messageId ? { ...msg, messageFeedback: result } : msg
        );
        conversationsContext.setMessages(currentConversationId, updatedMessages);
      }

      trackEvent("analysis_agent_message_feedback_v2", {
        dataset_id: contextId || "Unknown",
        conversation_id: currentConversationId,
        message_id: messageId,
        context: "dataset",
        feedback_type: feedback.feedback_type,
        feedback_value: feedback.feedback_value,
        has_text: !!feedback.text,
      });
    },
    [currentConversationId, api, conversationsContext, contextId]
  );

  const clearMessageFeedback = useCallback(
    async (messageId: string): Promise<void> => {
      if (!currentConversationId) {
        console.warn("Cannot clear feedback: no active conversation");
        return;
      }

      await api.deleteMessageFeedback(currentConversationId, messageId);

      // Update message state in context
      if (conversationsContext) {
        const currentMessages = conversationsContext.getMessages(currentConversationId);
        const updatedMessages = currentMessages.map((msg) =>
          msg.id === messageId ? { ...msg, messageFeedback: null } : msg
        );
        conversationsContext.setMessages(currentConversationId, updatedMessages);
      }

      trackEvent("analysis_agent_message_feedback_v2_cleared", {
        dataset_id: contextId || "Unknown",
        conversation_id: currentConversationId,
        message_id: messageId,
        context: "dataset",
      });
    },
    [currentConversationId, api, conversationsContext, contextId]
  );

  // Load MCP servers on mount
  useEffect(() => {
    let cancelled = false;

    api
      .listMcpServers()
      .then((servers) => {
        if (cancelled) {
          return;
        }
        setMcpServers(servers);

        // Pre-select default servers
        const defaultServerIds = servers.filter((s) => s.default).map((s) => s.id);
        setSelectedMcpServers(defaultServerIds);
      })
      .catch((error) => {
        console.error("Failed to load MCP servers:", error);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load skills on mount (only if skills feature is enabled)
  useEffect(() => {
    if (!skillsEnabled) {
      return;
    }

    let cancelled = false;

    setIsLoadingSkills(true);
    api
      .listSkills()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setSystemSkills(response.system);
        setUserSkills(response.user);
      })
      .catch((error) => {
        console.error("Failed to load skills:", error);
        setSystemSkills([]);
        setUserSkills([]);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSkills(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillsEnabled]);

  // Load conversations when contextId changes
  useEffect(() => {
    if (!contextId) {
      return;
    }

    let cancelled = false;

    loadConversations(contextId)
      .then((loaded) => {
        if (cancelled) {
          return;
        }

        if (loaded.length === 0) {
          // No conversations - create first one (will set loading false when done)
          createConversation().then(() => {
            setIsLoadingContextHistory(false);
          });
        } else if (!currentConversationId) {
          // Auto-select most recent (history loading effect will handle setting loading to false)
          setCurrentConversationId(loaded[0].id);
        }
      })
      .catch((error) => {
        console.error("Failed to load conversations:", error);
        // Set loading false on error
        setIsLoadingContextHistory(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextId]);

  // Load history when conversation changes or debugMode changes
  useEffect(() => {
    if (!currentConversationId) {
      return;
    }

    // Skip history loading if conversation is actively streaming.
    // The stream maintains complete state (prior messages + streaming content)
    // and will continue updating context. Don't clear or reload anything.
    if (conversationsContext?.isStreaming(currentConversationId)) {
      setIsLoadingContextHistory(false);
      return;
    }

    // Skip history loading if we already have cached messages for this conversation.
    // Messages are cached when: 1) streamed in this session, 2) previously loaded from API
    const cachedMessages = conversationsContext?.getMessages(currentConversationId) ?? [];
    if (cachedMessages.length > 0) {
      setIsLoadingContextHistory(false);
      return;
    }

    // Load history for conversations without cached messages
    if (conversationsContext) {
      // Capture the conversation ID to prevent race conditions
      const conversationIdForThisLoad = currentConversationId;

      // Get current optimistic messages BEFORE clearing
      const currentMessages = conversationsContext.getMessages(currentConversationId);
      const optimisticIds = conversationsContext.getOptimisticMessageIds(currentConversationId);
      const optimisticMessages = currentMessages.filter((msg) => optimisticIds.has(msg.id));

      // Clear messages immediately when switching conversations
      clearMessages();

      // Load history directly into context
      setIsLoadingContextHistory(true);
      (async () => {
        try {
          const response = await api.loadChatHistory(conversationIdForThisLoad);
          const loadedMessages: Message[] = [];

          for await (const event of readStreamEvents(response)) {
            const result = processStreamEvent(loadedMessages, event, {
              nextMessageId: () => nextMessageId("history"),
              debugMode,
            });

            if (result) {
              loadedMessages.length = 0;
              loadedMessages.push(...result.messages);
            }
          }

          // Mark all history messages as feedbackReady since they're already persisted
          const historyWithFeedbackReady = loadedMessages.map((msg) => ({
            ...msg,
            feedbackReady: true,
          }));

          // Merge history with optimistic messages
          // History messages are already in correct order from the API stream, so don't sort them.
          // Only append optimistic messages at the end (they're always newer than history).
          const mergedMessages =
            optimisticMessages.length > 0
              ? [...historyWithFeedbackReady, ...optimisticMessages]
              : historyWithFeedbackReady;

          // Set final messages in context (not marking as optimistic since history is confirmed)
          conversationsContext.setMessages(conversationIdForThisLoad, mergedMessages);
        } catch (error) {
          console.error("Failed to load chat history:", error);
          // Restore optimistic messages on error so user doesn't lose in-progress streams
          // History will be retried next time the conversation is viewed
          if (optimisticMessages.length > 0) {
            conversationsContext.setMessages(conversationIdForThisLoad, optimisticMessages);
          }
        } finally {
          // Only update loading state if we're still viewing this conversation.
          // Use ref to get current value, not the stale closure value.
          if (currentConversationIdRef.current === conversationIdForThisLoad) {
            setIsLoadingContextHistory(false);
          }
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId, debugMode]);

  // Derive isSendingMessage for use in effects and return value
  const isSendingMessage =
    conversationsContext && currentConversationId
      ? conversationsContext.isStreaming(currentConversationId)
      : false;

  // Reset isCancelling when streaming ends
  useEffect(() => {
    if (!isSendingMessage && isCancelling) {
      setIsCancelling(false);
    }
  }, [isSendingMessage, isCancelling]);

  // Return stable interface
  return {
    // State
    messages,
    conversations,
    currentConversationId,
    isCreatingConversation,
    isLoadingHistory: isLoadingContextHistory,
    isSendingMessage,
    isCancelling,
    mcpServers,
    selectedMcpServers,
    systemSkills,
    userSkills,
    isLoadingSkills,

    // Actions
    sendMessage,
    cancelMessage,
    createConversation,
    deleteConversation,
    selectConversation,
    renameConversation,
    toggleMcpServer,
    clearMessages,
    submitMessageFeedback,
    clearMessageFeedback,
    createSkill,
    updateSkill,
    deleteSkill,
    getSkill,
  };
}
